import {getTrustAccountMemberIneligibleReason} from '@sollapay/domain/rules';
import {TrustAccountMemberRole} from '@sollapay/enums';

import type {TeamMember} from '../types';
import type {
  TrustAccountMemberCandidateDTO,
  TrustAccountMemberDTO,
  TrustAccountMembersDTO,
  TrustAccountMemberUserDTO,
  TrustAccountSuggestedTeamDTO
} from '@sollapay/types';

export function canHoldRole(
  candidate: Pick<TrustAccountMemberCandidateDTO, 'ineligibleReasons'>,
  role: TrustAccountMemberRole
): boolean {
  return candidate.ineligibleReasons[role] === null;
}

export function memberDisplayName(member: {name?: string; email?: string}): string {
  return member.name ?? member.email ?? '';
}

export function otherMemberRole(role: TrustAccountMemberRole): TrustAccountMemberRole {
  return role === TrustAccountMemberRole.OPERATOR
    ? TrustAccountMemberRole.VIEWER
    : TrustAccountMemberRole.OPERATOR;
}

/** The suggested team's members who are still in the firm and can still hold their role, in its order. */
export function getCopyableTeam(
  suggestedTeam: Pick<TrustAccountSuggestedTeamDTO, 'members'>,
  candidates: TrustAccountMemberCandidateDTO[]
): TeamMember[] {
  const candidatesById = new Map(candidates.map(candidate => [candidate.userId, candidate]));
  return suggestedTeam.members.flatMap(({userId, role}) => {
    const candidate = candidatesById.get(userId);
    return candidate && canHoldRole(candidate, role) ? [{...candidate, role}] : [];
  });
}

/** Everyone on the account, trustee first. */
export function getRosterPeople(roster: TrustAccountMembersDTO): TrustAccountMemberUserDTO[] {
  return [...(roster.trustee ? [roster.trustee] : []), ...roster.members];
}

/** A saved member with the same per-role eligibility the server enforces. */
export function toTeamMember(member: TrustAccountMemberDTO): TeamMember {
  const ineligibleReasons = Object.fromEntries(
    Object.values(TrustAccountMemberRole).map(memberRole => [
      memberRole,
      getTrustAccountMemberIneligibleReason({firmRoleKey: member.firmRoleKey, memberRole})
    ])
  ) as TeamMember['ineligibleReasons'];
  return {...member, ineligibleReasons};
}

/** The trustee first (outside the stack), everyone else by the date they joined. */
export function splitRoster(roster: TrustAccountMembersDTO): {
  trustee: TrustAccountMemberUserDTO | null;
  teammates: TrustAccountMemberDTO[];
} {
  return {
    trustee: roster.trustee,
    teammates: [...roster.members].sort((a, b) => a.addedAt.localeCompare(b.addedAt))
  };
}
